import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { resetPassword } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import { Logo } from '@/components/Logo'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const { showToast } = useToast()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      showToast('Please enter your email address', 'error')
      return
    }

    setLoading(true)

    try {
      await resetPassword(email)
      setSent(true)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to send reset email', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setLoading(true)
    try {
      await resetPassword(email)
      showToast('Reset link sent again!')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to resend', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f6f6f8] dark:bg-[#111621] flex flex-col">
      {/* Floating Navbar */}
      <div className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4">
        <header className="flex items-center justify-between w-full max-w-[480px] bg-white/80 dark:bg-[#1c2230]/80 backdrop-blur-md border border-gray-200 dark:border-gray-800 rounded-full px-6 py-2.5 shadow-sm">
          <Link to="/">
            <Logo size="md" variant="auto" />
          </Link>
          <Link
            to="/login"
            className="bg-[#1754cf] text-white text-sm font-bold px-5 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            Sign In
          </Link>
        </header>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 pt-28">
        <div className="w-full max-w-[440px]">
          {/* Auth Card */}
          <div className="bg-white dark:bg-slate-900 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-200 dark:border-slate-800 rounded-xl p-8">
            {!sent ? (
              <>
                {/* Headline */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-[#1754cf]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="material-symbols-outlined text-[#1754cf] text-3xl">lock_reset</span>
                  </div>
                  <h1 className="text-slate-900 dark:text-white text-3xl font-bold tracking-tight mb-2">
                    Forgot Password?
                  </h1>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    No worries! Enter your email and we'll send you a reset link.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="flex flex-col gap-2">
                    <label className="text-slate-700 dark:text-slate-300 text-sm font-medium">
                      Email address
                    </label>
                    <div className="relative">
                      <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">
                        mail
                      </span>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full rounded-lg text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1754cf]/20 focus:border-[#1754cf] h-12 pl-10 pr-4 placeholder:text-slate-400 text-sm transition-all"
                        placeholder="name@company.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#1754cf] hover:bg-[#1754cf]/90 text-white font-semibold h-12 rounded-lg transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Reset Link</span>
                          <span className="material-symbols-outlined text-lg">send</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              /* Success State */
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-3xl">
                    check_circle
                  </span>
                </div>
                <h2 className="text-slate-900 dark:text-white text-xl font-bold mb-2">Check your email</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                  We've sent a password reset link to{' '}
                  <strong className="text-slate-700 dark:text-slate-200">{email}</strong>
                </p>
                <p className="text-slate-400 text-xs mb-4">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="text-[#1754cf] hover:underline font-medium disabled:opacity-50"
                  >
                    {loading ? 'sending...' : 'click to resend'}
                  </button>
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-[#1754cf] text-sm font-semibold hover:underline"
                >
                  <span className="material-symbols-outlined text-base">arrow_back</span>
                  Back to Sign In
                </Link>
              </div>
            )}
          </div>

          {/* Footer Link */}
          {!sent && (
            <div className="mt-8 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm hover:text-[#1754cf] transition-colors"
              >
                <span className="material-symbols-outlined text-base">arrow_back</span>
                Back to Sign In
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-slate-200 dark:border-slate-800 text-center">
        <p className="text-slate-400 text-xs">© {new Date().getFullYear()} URLzippy Inc. All rights reserved.</p>
      </footer>
    </div>
  )
}
